/**
 * Shipping Preview Worker Integration
 * 
 * Fetches real-time shipping rates from Cloudflare Worker endpoint
 * and updates cart UI elements accordingly.
 * 
 * Worker Response Structure:
 * {
 *   shippingCost: 490,                           // Cents
 *   shippingCostFormatted: "4.90 CHF",
 *   freeShippingThreshold: 20000,                // Cents
 *   freeShippingThresholdFormatted: "200.00 CHF",
 *   currentTotal: 19800,                         // Cents (excl. sale products for B2C)
 *   currentTotalFormatted: "198.00 CHF",
 *   remainingForFree: 200,                       // Cents until free shipping
 *   remainingForFreeFormatted: "2.00 CHF",
 *   isB2B: false,
 *   isFreeShipping: false,
 *   rates: [
 *     {
 *       name: "Versand",                         // Translated
 *       code: "b2c-reduced",                     // Service code
 *       price: 490,                              // Cents
 *       priceFormatted: "4.90 CHF",
 *       description: "In 2-4 Werktagen bei dir" // Translated
 *     }
 *   ]
 * }
 */

class ShippingPreviewManager {
  constructor() {
    this.endpoint = 'https://vergani-shipping-rates.ka-ching.workers.dev/preview-rates';
    this.debounceTimer = null;
    this.debounceDelay = 200; // Reduced from 300ms for snappier feel
    this.cacheTimeout = 300000; // 5min cache (reduces worker calls significantly)
    this.lastWorkerData = null; // Store last worker response
    this.abortController = null; // To cancel ongoing requests
    
    // Rate limiting: track last request time
    this.lastRequestTime = 0;
    this.minRequestInterval = 300; // Min 300ms between requests (balanced)
    
    // Initialize persistent cache from sessionStorage
    this._initCache();
    
    this._initEventListeners();
  }
  
  _initCache() {
    try {
      const stored = sessionStorage.getItem('shipping-preview-cache');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert stored array back to Map and filter expired entries
        this.cache = new Map(
          parsed
            .filter(([key, value]) => Date.now() - value.timestamp < this.cacheTimeout)
            .map(([key, value]) => [key, value])
        );
        
        // Restore last worker data if available
        const lastData = sessionStorage.getItem('shipping-preview-last');
        if (lastData) {
          this.lastWorkerData = JSON.parse(lastData);
        }
      } else {
        this.cache = new Map();
      }
    } catch (e) {
      // Safari private mode or storage quota exceeded
      this.cache = new Map();
    }
  }
  
  _saveCache() {
    try {
      // Convert Map to array for JSON storage
      const cacheArray = Array.from(this.cache.entries());
      sessionStorage.setItem('shipping-preview-cache', JSON.stringify(cacheArray));
      
      if (this.lastWorkerData) {
        sessionStorage.setItem('shipping-preview-last', JSON.stringify(this.lastWorkerData));
      }
    } catch (e) {
      // Ignore storage errors
    }
  }

  _initEventListeners() {
    document.addEventListener('cart:change', this._handleCartChange.bind(this));

    // Modules are deferred: DOMContentLoaded has usually already fired when this runs.
    if (window.cartData?.item_count > 0) {
      this._handleCartChange({ detail: { cart: window.cartData } });
    } else if (this.lastWorkerData) {
      this._updateShippingCostDisplay(this.lastWorkerData);
    }
  }

  _handleCartChange(event) {
    clearTimeout(this.debounceTimer);
    
    this.debounceTimer = setTimeout(() => {
      this._fetchShippingRates(event.detail.cart);
    }, this.debounceDelay);
  }

  async _fetchShippingRates(cart) {
    // Don't fetch for B2B customers (frontend check)
    // Worker will also validate this
    if (window.customerData?.b2b) {
      return;
    }

    const requestPayload = this._buildRequestPayload(cart);
    const cacheKey = this._getCacheKey(requestPayload);

    // Check cache first (before rate limit check)
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      this._updateUI(cached.data);
      return;
    }

    // Rate limiting: prevent requests more frequent than minRequestInterval
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.minRequestInterval) {
      // Too soon, schedule for later
      const delay = this.minRequestInterval - timeSinceLastRequest;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this._fetchShippingRates(cart);
      }, delay);
      return;
    }

    // Update last request time
    this.lastRequestTime = now;

    // Cancel any ongoing request
    if (this.abortController) {
      this.abortController.abort();
    }

    // Create new abort controller for this request
    this.abortController = new AbortController();

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Worker responded with ${response.status}`);
      }

      const data = await response.json();
      
      // Store last response globally
      this.lastWorkerData = data;
      
      // Cache result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });
      
      // Persist cache to sessionStorage
      this._saveCache();

      this._updateUI(data);
      
      // Dispatch custom event for other components
      document.dispatchEvent(new CustomEvent('shipping:preview-updated', {
        detail: data
      }));

    } catch (error) {
      // Ignore aborted requests
      if (error.name === 'AbortError') {
        return;
      }
      
      console.error('[ShippingPreview] Failed to fetch rates:', error);
      this._showFallback();
    } finally {
      // Clear abort controller if this was the active request
      if (this.abortController && !this.abortController.signal.aborted) {
        this.abortController = null;
      }
    }
  }

  _buildRequestPayload(cart) {
    const items = cart.items.map(item => ({
      product_id: parseInt(item.product_id),
      variant_id: item.variant_id ? parseInt(item.variant_id) : undefined,
      quantity: item.quantity,
      price: item.final_price || item.price // Price per unit in cents
    }));

    return {
      items,
      customerId: window.customerData?.id ? String(window.customerData.id) : null,
      locale: window.Shopify?.locale || 'de'
    };
  }

  _getCacheKey(payload) {
    return JSON.stringify(payload);
  }

  _updateUI(data) {
    // Update shipping cost display in cart drawer
    this._updateShippingCostDisplay(data);
    
    // Update free shipping bar
    this._updateFreeShippingBar(data);
  }

  _updateShippingCostDisplay(data) {
    const container = document.querySelector('[data-shipping-preview]');
    if (!container) return;

    const { 
      shippingCostFormatted, 
      isFreeShipping,
      rates 
    } = data;

    // Use the first rate or show formatted cost
    const primaryRate = rates && rates.length > 0 ? rates[0] : null;
    const displayName = primaryRate?.name || 'Versand';
    const displayCost = shippingCostFormatted || this._formatMoney(data.shippingCost || 0);

    container.innerHTML = `
      <span ${isFreeShipping ? 'class="bold text-success"' : ''}>
        ${this._escapeHtml(displayName)}
      </span>
      <span ${isFreeShipping ? 'class="bold text-success"' : ''}>
        ${displayCost}
      </span>
    `;
  }

  _updateFreeShippingBar(data) {
    const eventData = {
      remainingForFree: data.remainingForFree,
      isFreeShipping: data.isFreeShipping,
      freeShippingThreshold: data.freeShippingThreshold,
      currentTotal: data.currentTotal
    };

    // Broadcast to ALL free-shipping-bar elements via document event
    document.dispatchEvent(new CustomEvent('shipping:worker-rates-updated', {
      detail: eventData
    }));
  }

  _showFallback() {
    const container = document.querySelector('[data-shipping-preview]');
    if (container) {
      container.innerHTML = `
        <span class="text-subdued">Versandkosten werden beim Checkout berechnet</span>
      `;
    }
  }

  _formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.themeVariables?.settings?.moneyWithCurrencyFormat || '{{amount_with_currency}}');
    }
    // Fallback
    return `${(cents / 100).toFixed(2)} CHF`;
  }

  _escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize on load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.shippingPreviewManager = new ShippingPreviewManager();
  });
} else {
  window.shippingPreviewManager = new ShippingPreviewManager();
}

